import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import ClearSharpIcon from '@mui/icons-material/ClearSharp';
import { useTranslation } from 'react-i18next';
import { MaxLength } from '../../../../../_constants/maxLengthConst';
import { NgSampleData } from '../EditTypeModal';
import FolderIcon from '@mui/icons-material/Folder';
import { ACCEPT_TYPE_NG_SAMPLE } from '../../../../../_constants/manageConst';
import { LOADING_BACKDROP_STYLE } from '../../../../../_constants/styleConst';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

type DisplayNgSampleProps = {
  ngSample: NgSampleData;
  deleteNgSample: (data: number) => void;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    imageNo: number,
  ) => void;
  handleImageChange: (file: File, imageNo: number) => void;
  getFileName: (path: string) => string;
};

const DisplayNgSample: React.FC<DisplayNgSampleProps> = ({
  ngSample,
  deleteNgSample,
  handleInputChange,
  handleImageChange,
  getFileName,
}) => {
  const { t } = useTranslation();

  const onChangeFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    imageNo: number,
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    handleImageChange(file, imageNo);
  };

  return (
    <React.Fragment>
      <Grid
        size={6}
        sx={{
          width: '100%',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={() => deleteNgSample(ngSample.imageNo)}
          className="hover_delete_icon"
          sx={{
            position: 'absolute',
            right: '-15px',
            top: `calc(-5px - 1rem)`,
            bgcolor: '#FFF',
            display: 'none',
            width: '36px',
            height: '36px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ClearSharpIcon />
        </IconButton>
      </Grid>
      {ngSample.error ? (
        <Grid
          size={12}
          textAlign={'center'}
          sx={{ px: 1.5, pt: '2rem', height: '150px' }}
        >
          <Typography color="error">{ngSample.error}</Typography>
        </Grid>
      ) : ngSample.file ? (
        <React.Fragment>
          <Grid size={12} textAlign={'center'} sx={{ pt: '1rem' }}>
            <Box
              component="img"
              src={URL.createObjectURL(ngSample.file!)}
              alt="preview"
              className="preview_img"
              style={{
                width: '90%',
                height: '150px',
                objectFit: 'contain',
              }}
              sx={{ pointerEvents: 'none' }}
            />
          </Grid>
        </React.Fragment>
      ) : (
        <Grid size={12} sx={{ position: 'relative', height: '150px' }}>
          <Backdrop
            sx={{
              ...LOADING_BACKDROP_STYLE,
              zIndex: (theme) => theme.zIndex.drawer + 1,
              position: 'absolute',
            }}
            open={!ngSample.file}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Grid>
      )}
      <Grid size={12} textAlign={'center'}>
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: 'grey.500',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ngSample.file ? ngSample.file.name : getFileName(ngSample.imagePath)}
        </Typography>
      </Grid>
      <Grid
        size={12}
        textAlign={'center'}
        sx={{ padding: '12px 20px 0px 20px' }}
      >
        <TextField
          label={t('LBL_NG_SAMPLE_NAME', { 0: t('LBL_LANG_EN') })}
          sx={{
            bgcolor: 'warning.light',
            borderRadius: '6px',
            marginBottom: '1rem',
          }}
          fullWidth
          size="small"
          name="labelName"
          value={ngSample.labelName}
          onChange={(e) => handleInputChange(e, ngSample.imageNo)}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.NG_SAMPLE_NAME,
            },
          }}
        />
        <TextField
          label={t('LBL_NG_SAMPLE_NAME', { 0: t('LBL_LANG_JA') })}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: '6px',
            marginBottom: '1rem',
          }}
          fullWidth
          size="small"
          name="labelNameJp"
          value={ngSample.labelNameJp}
          onChange={(e) => handleInputChange(e, ngSample.imageNo)}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageInspectionItem.NG_SAMPLE_NAME,
            },
          }}
        />
      </Grid>
      <Grid size={12} textAlign="center" sx={{ padding: '0 20px 1rem 20px' }}>
        <Button
          component="label"
          role={undefined}
          variant="contained"
          fullWidth
          sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}
          tabIndex={-1}
          endIcon={<FolderIcon color="primary" />}
        >
          {t('BTN_IMAGE_CHANGE')}
          <VisuallyHiddenInput
            type="file"
            accept={ACCEPT_TYPE_NG_SAMPLE}
            onChange={(e) => onChangeFile(e, ngSample.imageNo)}
          />
        </Button>
      </Grid>
    </React.Fragment>
  );
};

export default DisplayNgSample;
